import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';

import { buildAdminIntakeHubPath } from '../adminJourneyRouting';
import type { IntakeFleetCounts, IntakeFleetOrganization, IntakeFleetResponse } from '../types';

import { EhrOrganizationStatus } from './ehr/EhrFleetSummary';
import { IntakePill } from './shell/IntakePill';
import { footnoteSx, metaSx, tableHeadSx, wf } from './shell/tokens';
import { FLEET_STAGE_TILES, type FleetStage } from './fleetStages';
import { GoLiveStatus } from './GoLiveStatus';
import { useGoLiveLoadWindow } from './useGoLiveLoadWindow';

const SERVICE_LABELS: Record<string, string> = {
  jotbill: 'JotBill',
  jotcred: 'JotCred',
  jotsite: 'JotSite',
};

export type FleetTrack = 'overview' | 'credentialing' | 'ehr-import';
export type FleetSortBy = 'last_activity' | 'name';
type FleetSortOrder = 'asc' | 'desc';
const headCellSx = { ...tableHeadSx, borderBottom: `1px solid ${wf.lineSoft}`, py: '11px' };
const bodyCellSx = {
  borderBottom: `1px solid ${wf.lineSoft}`,
  py: '14px',
  verticalAlign: 'top',
  color: wf.body,
};

// A 1px row height lets cell content fill the row, so each action link sits at the bottom.
const actionCellSx = { ...bodyCellSx, height: '1px' };
const actionStackSx = { height: '100%', alignItems: 'flex-start' };
const actionLinkSx = { mt: 'auto', px: 0, textTransform: 'none', fontWeight: 700 };
const tableWrapSx = {
  border: `1px solid ${wf.line}`,
  borderRadius: '10px',
  overflow: 'hidden',
  backgroundColor: wf.surface,
};

function credentialingState(row: IntakeFleetOrganization) {
  const counts = row.credentialing;
  if (!counts) return { label: 'Records unavailable', tone: 'slate' as const };
  const total = counts.total ?? 0;
  if (!total) return { label: 'Not started', tone: 'slate' as const };
  if ((counts.complete ?? 0) >= total) return { label: 'Complete', tone: 'green' as const };
  if ((counts.complete ?? 0) > 0 || (counts.in_progress ?? 0) > 0)
    return { label: 'In progress', tone: 'indigo' as const };
  return { label: 'Not started', tone: 'slate' as const };
}

function lastActivity(row: IntakeFleetOrganization) {
  return row.last_activity_at ? new Date(row.last_activity_at).toLocaleDateString() : 'No activity';
}

function SortHeader({
  label,
  field,
  sort,
}: {
  label: string;
  field: FleetSortBy;
  sort?: {
    by: FleetSortBy;
    order: FleetSortOrder;
    onSort: (by: FleetSortBy, order: FleetSortOrder) => void;
  };
}) {
  // All-organization pages keep stable ID order, so only the active view sorts.
  if (!sort) return <TableCell sx={headCellSx}>{label}</TableCell>;
  const active = sort.by === field;
  const initial = field === 'name' ? 'asc' : 'desc';
  return (
    <TableCell sx={headCellSx} sortDirection={active ? sort.order : false}>
      <TableSortLabel
        active={active}
        direction={active ? sort.order : initial}
        onClick={() =>
          sort.onSort(field, active ? (sort.order === 'asc' ? 'desc' : 'asc') : initial)
        }
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

function PracticeCell({
  row,
  canVerify,
  verificationPath,
}: {
  row: IntakeFleetOrganization;
  canVerify: boolean;
  verificationPath: string;
}) {
  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: wf.ink }}>
        {row.organization_name}
      </Typography>
      <Typography sx={metaSx}>
        {row.selected_services == null
          ? 'Selection unknown'
          : row.selected_services.length
            ? row.selected_services.map(service => SERVICE_LABELS[service] ?? service).join(', ')
            : 'No services selected'}
      </Typography>
      {!canVerify ? (
        <Typography sx={metaSx}>Admin access required</Typography>
      ) : row.verification ? (
        <Stack spacing={0.5} sx={{ alignItems: 'flex-start', pt: '2px' }}>
          <IntakePill
            tone={
              row.verification.state === 'verified'
                ? 'green'
                : row.verification.state === 'awaiting'
                  ? 'amber'
                  : 'slate'
            }
            label={
              row.verification.state === 'verified'
                ? 'Verified'
                : row.verification.state === 'awaiting'
                  ? 'Awaiting verification'
                  : 'Incomplete'
            }
          />
          <Typography sx={metaSx}>
            {row.verification.provider_counts.verified} of {row.verification.provider_counts.total}{' '}
            roster providers verified
          </Typography>
          <Button
            size="small"
            component={Link}
            to={verificationPath}
            sx={{ px: 0, textTransform: 'none', fontWeight: 700 }}
          >
            Review
          </Button>
        </Stack>
      ) : (
        <Typography sx={metaSx}>No eligible verification evidence</Typography>
      )}
    </Stack>
  );
}

function CredentialingCard({
  row,
  recordsPath,
}: {
  row: IntakeFleetOrganization;
  recordsPath: string;
}) {
  const counts = row.credentialing;
  const total = counts?.total ?? 0;
  const complete = counts?.complete ?? 0;
  const inProgress = counts?.in_progress ?? 0;
  const notStarted = counts?.not_started ?? Math.max(0, total - complete - inProgress);
  const state = credentialingState(row);
  const segments = [
    { n: complete, color: wf.green },
    { n: inProgress, color: wf.blue },
    { n: notStarted, color: wf.track },
  ].filter(segment => segment.n > 0);
  return (
    <Box
      sx={{
        ...tableWrapSx,
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '14px 20px',
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ minWidth: 0, flex: '1 1 260px' }}>
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 700, color: wf.ink }}>
            {row.organization_name}
          </Typography>
          <IntakePill tone={state.tone} label={state.label} />
        </Stack>
        <Typography sx={metaSx}>
          {total} recorded · {complete} complete · {inProgress} in progress · {notStarted} not
          started · Last activity {lastActivity(row)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: '3px', width: 180 }}>
        {segments.length ? (
          segments.map(segment => (
            <Box
              key={segment.color}
              sx={{
                flex: `${segment.n} 1 0`,
                height: '6px',
                borderRadius: '3px',
                backgroundColor: segment.color,
              }}
            />
          ))
        ) : (
          <Box sx={{ flex: 1, height: '6px', borderRadius: '3px', backgroundColor: wf.track }} />
        )}
      </Box>
      <Button
        size="small"
        component={Link}
        to={recordsPath}
        aria-label={`Open records for ${row.organization_name}`}
        sx={{ flex: '0 0 auto', textTransform: 'none', fontWeight: 700 }}
      >
        Open records
      </Button>
    </Box>
  );
}

export function StaffIntakeFleet({
  data,
  counts,
  loading,
  failed,
  search,
  page,
  perPage,
  showAll,
  sortBy,
  sortOrder,
  stage,
  track,
  returnTo,
  canVerify,
  onSearch,
  onPage,
  onShowAll,
  onSort,
  onStage,
  onRefresh,
}: {
  data?: IntakeFleetResponse;
  /** Stage counts for the current search, kept while a tile or sort change reloads the page. */
  counts?: IntakeFleetCounts | null;
  loading: boolean;
  failed: boolean;
  search: string;
  page: number;
  perPage: number;
  showAll: boolean;
  sortBy: FleetSortBy;
  sortOrder: FleetSortOrder;
  stage: FleetStage | null;
  track: FleetTrack;
  returnTo: string;
  canVerify: boolean;
  onSearch: (value: string) => void;
  onPage: (value: number) => void;
  onShowAll: (value: boolean) => void;
  onSort: (by: FleetSortBy, order: FleetSortOrder) => void;
  onStage: (stage: FleetStage | null) => void;
  onRefresh: () => void;
}) {
  const path = (organizationId: string, target: FleetTrack | 'billing' | 'verification') =>
    buildAdminIntakeHubPath({
      organizationId,
      stage:
        target === 'overview'
          ? 'agreements'
          : target === 'verification'
            ? 'verification'
            : 'go-live',
      statusTab: target === 'overview' || target === 'verification' ? undefined : target,
      returnTo,
    });
  const rows = data?.items ?? [];
  const organizationIds = useMemo(
    () => (track === 'overview' ? (data?.items ?? []).map(row => row.organization_id) : []),
    [data?.items, track],
  );
  const goLiveWindow = useGoLiveLoadWindow(organizationIds);
  const sort = showAll ? undefined : { by: sortBy, order: sortOrder, onSort };
  const empty = data && rows.length === 0;
  const emptyState = empty ? (
    <Box sx={{ ...tableWrapSx, p: 4, textAlign: 'center' }}>
      <Typography>
        {data.total
          ? 'No organizations on this page.'
          : stage && !showAll
            ? search
              ? 'No practices in this stage match this search.'
              : 'No practices are in this stage.'
            : showAll || search
              ? 'No organizations match this search.'
              : 'No organizations have intake activity yet.'}
      </Typography>
      {(data.total ?? 0) > 0 && <Button onClick={() => onPage(1)}>First page</Button>}
    </Box>
  ) : null;
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2}>
        <TextField
          label="Search organizations"
          size="small"
          value={search}
          onChange={event => onSearch(event.target.value)}
          slotProps={{ htmlInput: { maxLength: 100 } }}
          sx={{ flex: 1, backgroundColor: wf.surface }}
        />
        <FormControlLabel
          control={<Switch checked={showAll} onChange={event => onShowAll(event.target.checked)} />}
          label="Show all organizations"
          sx={{ flexShrink: 0 }}
        />
        <Button variant="outlined" disabled={loading} onClick={onRefresh}>
          Refresh
        </Button>
      </Stack>
      <Typography sx={{ ...footnoteSx, maxWidth: 'none' }} aria-live="polite">
        {!showAll
          ? counts
            ? `${counts.total ?? 0} ${counts.total === 1 ? 'organization' : 'organizations'} with intake enrollments or credentialing records${search ? ' matching search' : ''}${stage && data ? ` · ${data.total ?? 0} in the selected stage` : ''}`
            : 'Organizations with intake enrollments or credentialing records'
          : data
            ? `${data.total == null ? 'Unknown total of' : `About ${data.total}`} organizations matching search · includes organizations without intake configuration`
            : 'All organizations, including those without intake configuration'}
      </Typography>
      {track === 'ehr-import' && (
        <Alert severity="info">
          EHR migration applies to the organization. Staff import and verify the uploaded files,
          with progress reported from its import session. Open the Intake Hub to review batches.
        </Alert>
      )}
      {failed && (
        <Alert
          severity={data ? 'warning' : 'error'}
          action={
            <Button color="inherit" onClick={onRefresh}>
              Retry
            </Button>
          }
        >
          {data
            ? 'Refresh failed. Previously loaded evidence remains visible and may be stale.'
            : 'Could not load organizations. Retry to check their stored evidence.'}
        </Alert>
      )}
      {loading && <LinearProgress aria-label="Loading organization evidence" />}
      {data && track === 'credentialing' && (
        <Stack spacing={1.75}>
          {rows.map(row => (
            <CredentialingCard
              key={row.organization_id}
              row={row}
              recordsPath={path(row.organization_id, 'credentialing')}
            />
          ))}
          {emptyState}
          <Typography sx={footnoteSx}>
            One card per practice. The bar is the recorded provider × payer entries: complete, in
            progress, and not started. The practice is complete only when every entry is complete.
            The provider × payer records live in each organization’s Intake Hub.
          </Typography>
        </Stack>
      )}
      {track === 'overview' && !showAll && (
        <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(158px, 1fr))',
              gap: '12px',
            }}
          >
            {FLEET_STAGE_TILES.map(tile => {
              const active = stage === tile.value;
              const hidden = tile.adminOnly && !canVerify;
              const count = counts?.[tile.value ?? 'total'];
              return (
                <ButtonBase
                  key={tile.label}
                  disabled={hidden}
                  onClick={() => onStage(active ? null : tile.value)}
                  aria-pressed={active}
                  sx={{
                    ...tableWrapSx,
                    display: 'block',
                    textAlign: 'left',
                    padding: '14px 16px',
                    borderColor: active ? wf.blue : wf.line,
                    boxShadow: active ? `0 0 0 1px ${wf.blue} inset` : 'none',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '24px',
                      fontWeight: 700,
                      letterSpacing: '-0.02em',
                      color: tile.color,
                    }}
                  >
                    {count ?? '—'}
                  </Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: wf.ink }}>
                    {tile.label}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: wf.meta, lineHeight: 1.45 }}>
                    {hidden ? 'Admin access required' : tile.hint}
                  </Typography>
                </ButtonBase>
              );
            })}
          </Box>
          <Typography sx={{ ...footnoteSx, mt: '10px' }}>
            Practice counts for your search, across every result page. Selecting a tile filters the
            practices below.
          </Typography>
        </Box>
      )}
      {data && track === 'overview' && (
        <>
          <TableContainer sx={tableWrapSx}>
            <Table aria-label="Staff intake organizations">
              <TableHead>
                <TableRow sx={{ backgroundColor: wf.surfaceAlt }}>
                  <SortHeader label="Practice" field="name" sort={sort} />
                  <TableCell sx={headCellSx}>Billing</TableCell>
                  <TableCell sx={headCellSx}>Credentialing</TableCell>
                  <TableCell sx={headCellSx}>EHR migration</TableCell>
                  <TableCell sx={headCellSx}>Go live</TableCell>
                  <SortHeader label="Last activity" field="last_activity" sort={sort} />
                  <TableCell sx={headCellSx}>Intake Hub</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => {
                  const state = credentialingState(row);
                  return (
                    <TableRow key={row.organization_id}>
                      <TableCell sx={bodyCellSx}>
                        <PracticeCell
                          row={row}
                          canVerify={canVerify}
                          verificationPath={path(row.organization_id, 'verification')}
                        />
                      </TableCell>
                      <TableCell sx={actionCellSx}>
                        <Stack spacing={0.5} sx={actionStackSx}>
                          <IntakePill
                            tone={row.billing_connection_recorded ? 'green' : 'slate'}
                            label={
                              row.billing_connection_recorded == null
                                ? 'Connection unknown'
                                : row.billing_connection_recorded
                                  ? 'Connection recorded'
                                  : 'Not connected'
                            }
                          />
                          <Button
                            size="small"
                            component={Link}
                            to={path(row.organization_id, 'billing')}
                            sx={actionLinkSx}
                          >
                            Review billing
                          </Button>
                        </Stack>
                      </TableCell>
                      <TableCell sx={actionCellSx}>
                        <Stack spacing={0.5} sx={actionStackSx}>
                          <IntakePill tone={state.tone} label={state.label} />
                          <Typography sx={metaSx}>
                            {row.credentialing ? (
                              <>
                                {row.credentialing.total ?? 'Unknown'} recorded ·{' '}
                                {row.credentialing.complete ?? 'Unknown'} complete
                              </>
                            ) : (
                              'Credentialing evidence unavailable'
                            )}
                          </Typography>
                          <Button
                            size="small"
                            component={Link}
                            to={path(row.organization_id, 'credentialing')}
                            sx={actionLinkSx}
                          >
                            Review records
                          </Button>
                        </Stack>
                      </TableCell>
                      <TableCell sx={actionCellSx}>
                        <Stack spacing={0.5} sx={actionStackSx}>
                          <EhrOrganizationStatus organizationId={row.organization_id} />
                          <Button
                            size="small"
                            component={Link}
                            to={path(row.organization_id, 'ehr-import')}
                            sx={actionLinkSx}
                          >
                            Review uploads
                          </Button>
                        </Stack>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <GoLiveStatus
                          organizationId={row.organization_id}
                          enabled={goLiveWindow.enabled.has(row.organization_id)}
                          onSettled={goLiveWindow.markSettled}
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Typography sx={metaSx}>{lastActivity(row)}</Typography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Button
                          component={Link}
                          to={path(row.organization_id, track)}
                          variant="outlined"
                          size="small"
                          sx={{ textTransform: 'none', borderRadius: '6px', whiteSpace: 'nowrap' }}
                        >
                          Open Intake Hub
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {empty && emptyState}
          </TableContainer>
          <Typography sx={footnoteSx}>
            Billing reflects whether a payer connection date is recorded. Credentialing counts the
            recorded provider × payer entries. A practice is ready to bill once its first
            payer&rsquo;s Stedi enrollments are all Live.
          </Typography>
        </>
      )}
      {data && track === 'ehr-import' && (
        <>
          <TableContainer sx={tableWrapSx}>
            <Table aria-label="Staff intake organizations">
              <TableHead>
                <TableRow sx={{ backgroundColor: wf.surfaceAlt }}>
                  <SortHeader label="Practice" field="name" sort={sort} />
                  <TableCell sx={headCellSx}>Status</TableCell>
                  <SortHeader label="Last activity" field="last_activity" sort={sort} />
                  <TableCell sx={headCellSx}>Intake Hub</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.organization_id}>
                    <TableCell sx={bodyCellSx}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: wf.ink }}>
                        {row.organization_name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <EhrOrganizationStatus organizationId={row.organization_id} />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Typography sx={metaSx}>{lastActivity(row)}</Typography>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Button
                        component={Link}
                        to={path(row.organization_id, track)}
                        variant="outlined"
                        size="small"
                        sx={{ textTransform: 'none', borderRadius: '6px', whiteSpace: 'nowrap' }}
                      >
                        Open Intake Hub
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {empty && emptyState}
          </TableContainer>
          <Typography sx={footnoteSx}>
            Status reflects the uploaded files and their import session.
          </Typography>
        </>
      )}
      <TablePagination
        component="div"
        count={-1}
        page={page - 1}
        rowsPerPage={perPage}
        rowsPerPageOptions={[perPage]}
        onPageChange={(_event, next) => onPage(next + 1)}
        labelRowsPerPage="Organizations per page"
        labelDisplayedRows={() => {
          const size = data?.items.length ?? 0;
          const from = size ? (page - 1) * perPage + 1 : 0;
          const to = size ? (page - 1) * perPage + size : 0;
          return `${from}–${to} of ${showAll ? 'about ' : ''}${data?.total ?? 0}`;
        }}
        slotProps={{
          actions: {
            nextButton: { disabled: loading || failed || (data?.items.length ?? 0) < perPage },
          },
        }}
      />
    </Stack>
  );
}
